import React, { useCallback, useRef, useState } from 'react';
import type { CaptionCue } from '../../services/studioService';
import { cueAt } from './captionCues';

/**
 * THE FINISHED FILM, CAPTIONED.
 *
 * Vertical drama is watched MUTED FIRST — a viewer decides whether to stay before they decide whether to turn
 * sound on — so captions are how most of an audience hears an episode at all. Ours are built at render time
 * from the dialogue the writer wrote and the slots the clips were generated at, so there is nothing to fetch
 * and nothing to transcribe.
 *
 * WHY NOT A `<track>` ELEMENT. A native text track needs the caption file served cross-origin with CORS
 * agreeing, and buys nothing here: the cues are already in memory beside the video, and drawing them
 * ourselves means they are styled like the rest of the product rather than like the browser's default. A
 * WebVTT export is worth building the day something outside our player needs one.
 *
 * The overlay sits INSIDE the video's own box and is `pointer-events-none`, so it can never swallow a click
 * meant for the controls underneath it.
 */
export interface CaptionedVideoProps {
  src: string;
  captions?: CaptionCue[] | null;
  className?: string;
}

const CaptionedVideo: React.FC<CaptionedVideoProps> = ({ src, captions, className }) => {
  const [line, setLine] = useState('');
  const ref = useRef<HTMLVideoElement>(null);

  // Reading the clock is cheap; finding the cue is a scan over a handful of lines. No effect, no interval —
  // the element tells us when it moved.
  const onTime = useCallback(() => {
    const ms = (ref.current?.currentTime || 0) * 1000;
    setLine(cueAt(captions, ms)?.text || '');
  }, [captions]);

  return (
    <div className={`relative ${className || ''}`}>
      <video
        ref={ref}
        src={src}
        controls
        data-testid="episode-video"
        onTimeUpdate={onTime}
        className="w-full rounded-xl border border-secondary-200 dark:border-secondary-800"
      />
      {line && (
        <p
          data-testid="caption-line"
          aria-live="polite"
          /* Above the controls rather than over them, and centred on the safe part of a 9:16 frame. The
             black plate is what keeps a caption legible over a bright shot — white text alone disappears. */
          className="pointer-events-none absolute inset-x-0 bottom-12 mx-auto w-fit max-w-[92%] rounded bg-black/75 px-2 py-1
                     text-center text-[13px] font-medium leading-snug text-white"
        >
          {line}
        </p>
      )}
    </div>
  );
};

export default CaptionedVideo;
